import type { ReactNode } from 'react'
import { cn } from '@/lib/utils/helpers'

/** Reusable pill toggle switch — same visual language as the Product add/edit form toggles. */
export function ToggleSwitch({
  checked,
  onChange,
  disabled = false,
  onColorClass = 'bg-[rgb(21_151_255)]',
  offColorClass = 'bg-slate-300',
  thumbIcon,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: {
  checked: boolean
  onChange: () => void
  disabled?: boolean
  /** Track color when checked (default app blue). */
  onColorClass?: string
  /** Track color when unchecked. */
  offColorClass?: string
  /** Optional icon rendered inside the thumb for the current state (e.g. a Lock/Unlock glyph). */
  thumbIcon?: (checked: boolean) => ReactNode
  'aria-label'?: string
  'data-testid'?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onChange}
      data-testid={dataTestId}
      className={cn(
        'relative inline-flex h-[26px] w-[46px] shrink-0 items-center rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(21_151_255)]/40 focus-visible:ring-offset-2',
        checked ? onColorClass : offColorClass,
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      )}
    >
      <span
        className={cn(
          'inline-flex h-[20px] w-[20px] transform items-center justify-center rounded-full bg-white shadow-sm transition-transform duration-200',
          checked ? 'translate-x-[23px]' : 'translate-x-[3px]',
        )}
      >
        {thumbIcon ? thumbIcon(checked) : null}
      </span>
    </button>
  )
}
